import { React, useState, useEffect, useRef } from 'react';

import { useRecoilValue } from 'recoil';
import { TransientState } from '../../storage';

// mui
import {
    Box,
    Divider,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';

// client-shared
import { ContainerScroll } from '@goldblack/client-comps';

// components
import { AdminMenuBar, CommonYesNoDialog, PillTabs } from '..';

// api
import { AdminObjectList, AdminConfigUpdate } from '../../api';

// shared
import { conf } from '@goldblack/shared';

function ConfigValueCell({ rowKey, value, resetKey, onChange, selected, hovered }) {
    const isBool = typeof value === 'boolean';
    const initial = useRef(isBool ? value : typeof value === 'object' ? JSON.stringify(value) : String(value ?? ''));
    const [changed, setChanged] = useState(false);

    const handleInput = (e) => {
        const newVal = e.currentTarget.textContent;
        const isChanged = newVal !== String(initial.current);
        setChanged(isChanged);
        onChange(rowKey, newVal, isChanged);
    };

    const handleSelect = (e) => {
        const newVal = e.target.value;
        const isChanged = newVal !== String(initial.current);
        setChanged(isChanged);
        onChange(rowKey, newVal, isChanged);
    };

    if (isBool) {
        return (
            <Select
                key={resetKey}
                size='small'
                defaultValue={String(initial.current)}
                onChange={handleSelect}
                sx={{
                    fontSize: '0.8rem',
                    height: 28,
                    color: changed ? '#ff8a80' : 'text.primary',
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                    },
                    '& .MuiSelect-select': {
                        textAlign: 'left',
                        pl: '6px',
                        pr: '20px',
                        py: '2px',
                    },
                }}>
                <MenuItem value='true' sx={{ fontSize: '0.8rem' }}>
                    true
                </MenuItem>
                <MenuItem value='false' sx={{ fontSize: '0.8rem' }}>
                    false
                </MenuItem>
            </Select>
        );
    }

    return (
        <Paper
            key={resetKey}
            contentEditable
            suppressContentEditableWarning
            onInput={handleInput}
            dangerouslySetInnerHTML={{ __html: String(initial.current) }}
            sx={{
                fontSize: '0.8rem',
                px: '6px',
                py: '2px',
                color: changed ? '#ff8a80' : 'text.primary',
                border: 'none',
                outline: 'none',
                wordBreak: 'break-all',
                cursor: 'text',
                boxShadow: 'none',
                bgcolor: 'transparent',
                backgroundImage: 'none',
            }}
        />
    );
}

function AdditionRow({ id, onKeyChange, onValueChange, selected, onSelect }) {
    return (
        <TableRow
            hover
            selected={selected}
            onClick={onSelect}
            sx={{ cursor: 'pointer', '& td': { borderBottom: 'none' } }}>
            <TableCell sx={{ p: '4px 16px' }}>
                <TextField
                    size='small'
                    placeholder='key'
                    variant='standard'
                    onChange={(e) => onKeyChange(id, e.target.value)}
                    slotProps={{
                        input: {
                            sx: { fontSize: '0.8rem', height: 28 },
                        },
                    }}
                    sx={{ width: '100%' }}
                />
            </TableCell>
            <TableCell sx={{ p: '4px 16px' }}>
                <TextField
                    size='small'
                    placeholder='value'
                    variant='standard'
                    onChange={(e) => onValueChange(id, e.target.value)}
                    slotProps={{
                        input: {
                            sx: { fontSize: '0.8rem', height: 28 },
                        },
                    }}
                    sx={{ width: '100%' }}
                />
            </TableCell>
            <TableCell />
        </TableRow>
    );
}

function ConfigTable({ service, type, configs, onRefresh }) {
    const [changes, setChanges] = useState({});
    const [additions, setAdditions] = useState([]);
    const [selectedKey, setSelectedKey] = useState(null);
    const [hoveredKey, setHoveredKey] = useState(null);
    const [resetKey, setResetKey] = useState(0);
    const [openReset, setOpenReset] = useState(false);
    const addIdRef = useRef(0);

    const handleValueChange = (key, newVal, isChanged) => {
        setChanges((prev) => {
            const next = { ...prev };
            if (isChanged) next[key] = newVal;
            else delete next[key];
            return next;
        });
    };

    const handleAdditionKeyChange = (id, key) => {
        setAdditions((prev) => prev.map((a) => (a.id === id ? { ...a, key } : a)));
    };

    const handleAdditionValueChange = (id, value) => {
        setAdditions((prev) => prev.map((a) => (a.id === id ? { ...a, value } : a)));
    };

    const clickAdd = () => {
        const id = ++addIdRef.current;
        setAdditions((prev) => [...prev, { id, key: '', value: '' }]);
    };

    const clickDelete = () => {
        if (!selectedKey) return;
        const additionId = typeof selectedKey === 'number' ? selectedKey : null;
        if (additionId !== null) {
            setAdditions((prev) => prev.filter((a) => a.id !== additionId));
            setSelectedKey(null);
        } else {
            AdminConfigUpdate(service, 'delete', type, selectedKey, null, () => {
                onRefresh();
                setSelectedKey(null);
            });
        }
    };

    const clickUpdate = () => {
        const allChanges = { ...changes };
        additions.forEach(({ key, value }) => {
            if (key.trim()) allChanges[key] = value;
        });
        if (Object.keys(allChanges).length === 0) return;
        AdminConfigUpdate(service, 'update', type, null, allChanges, () => {
            setChanges({});
            setAdditions([]);
            setResetKey((k) => k + 1);
            onRefresh();
        });
    };

    const clickDiscard = () => {
        setChanges({});
        setAdditions([]);
        setResetKey((k) => k + 1);
        setSelectedKey(null);
    };

    const clickReset = () => setOpenReset(true);

    const doReset = () => {
        // Replaces this config type with source defaults, pruning stale keys.
        setOpenReset(false);
        AdminConfigUpdate(service, 'reset', type, null, null, () => {
            setChanges({});
            setAdditions([]);
            setResetKey((k) => k + 1);
            setSelectedKey(null);
            onRefresh();
        });
    };

    if (!configs) return null;

    const rows = Object.entries(configs).sort(([a], [b]) => a.localeCompare(b));
    const descs = conf.descriptions?.[service]?.[type] ?? {};

    const apiItems = [
        { label: 'Add', onClick: clickAdd },
        { label: 'Delete', onClick: clickDelete },
        { label: 'Update', onClick: clickUpdate },
        { label: 'Discard', onClick: clickDiscard },
        { label: 'Reset', onClick: clickReset },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <AdminMenuBar apiItems={apiItems} />
            <ContainerScroll sx={{ flex: 1 }}>
                <TableContainer sx={{ mt: 0 }}>
                    <Table size='small' sx={{ '& td, & th': { border: 1, borderColor: 'divider' } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, width: '25%' }}>Key</TableCell>
                                <TableCell sx={{ fontWeight: 600, width: '25%' }}>Value</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Desc</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 && additions.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} align='center' sx={{ color: 'text.secondary' }}>
                                        No configs
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map(([key, value]) => (
                                    <TableRow
                                        key={key}
                                        hover
                                        selected={selectedKey === key}
                                        onClick={() => setSelectedKey(key)}
                                        onMouseEnter={() => setHoveredKey(key)}
                                        onMouseLeave={() => setHoveredKey(null)}
                                        sx={{
                                            cursor: 'pointer',
                                            '& td': { borderBottom: 'none' },
                                        }}>
                                        <TableCell sx={{ fontSize: '0.8rem' }}>{key}</TableCell>
                                        <TableCell sx={{ p: '4px 16px' }}>
                                            <ConfigValueCell
                                                key={`${resetKey}-${key}`}
                                                rowKey={key}
                                                value={value}
                                                resetKey={resetKey}
                                                onChange={handleValueChange}
                                                selected={selectedKey === key}
                                                hovered={hoveredKey === key}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.8rem' }}>{descs[key] ?? ''}</TableCell>
                                    </TableRow>
                                ))
                            )}
                            {additions.map((a) => (
                                <AdditionRow
                                    key={a.id}
                                    id={a.id}
                                    selected={selectedKey === a.id}
                                    onSelect={() => setSelectedKey(a.id)}
                                    onKeyChange={handleAdditionKeyChange}
                                    onValueChange={handleAdditionValueChange}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </ContainerScroll>
            <CommonYesNoDialog
                open={openReset}
                onClose={() => setOpenReset(false)}
                title='Reset Config'
                desc={`Reset ${service}/${type} config to defaults? Custom keys will be removed.`}
                onYes={doReset}
                onNo={() => setOpenReset(false)}
            />
        </Box>
    );
}

const ACCOUNT_CONFIG_TABS = [
    { value: 'client', label: 'Client' },
    { value: 'server', label: 'Server' },
];

function AdminConfig() {
    const [tab, setTab] = useState('client');
    const transient = useRecoilValue(TransientState);

    const configs = tab === 'client' ? transient.account.client.data : transient.account.server.data;
    const meta = (tab === 'client' ? transient.account.client.meta : transient.account.server.meta) ?? {};
    const currentHash = meta.hash ?? '—';

    useEffect(() => {
        AdminObjectList('account', 'client');
        AdminObjectList('account', 'server');
    }, []);

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <PillTabs value={tab} onChange={setTab} tabs={ACCOUNT_CONFIG_TABS} />
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <ConfigTable
                    service='account'
                    type={tab}
                    configs={configs}
                    onRefresh={() => AdminObjectList('account', tab)}
                />
            </Box>
            <Divider />
            <Box sx={{ px: 2, py: 0.5 }}>
                <Typography variant='caption' color='text.secondary'>
                    hash: {currentHash}
                </Typography>
            </Box>
        </Box>
    );
}

export { ConfigTable, AdminConfig };
